import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, MessageCircle, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/i18n";
import { BRAND, scrollToSection, whatsappLink } from "@/constants/brand";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const waMsg = lang === "pt"
    ? "Olá! Gostaria de agendar uma reunião online para falar sobre soluções em tecnologia."
    : "Hello! I would like to schedule an online meeting to discuss technology solutions.";

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.04)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />

      <motion.div style={{ opacity: contentOpacity }} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="font-tech text-xs tracking-[0.18em] text-emerald-300">{t("hero.badge")}</span>
            </motion.div>

            <h1 data-testid="hero-heading-title" className="font-display text-4xl font-light leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.25} className="text-metallic">{t("hero.title1")}</MaskedLine>
              <MaskedLine delay={0.4} className="text-neon font-medium italic">{t("hero.title2")}</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-schedule-meeting-button"
                onClick={() => scrollToSection("agendar")}
                className="glow-button flex items-center gap-2.5 rounded-full border border-emerald-400/50 bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#030706] hover:bg-emerald-400"
              >
                <CalendarCheck className="h-4.5 w-4.5" />
                {t("hero.ctaSchedule")}
              </button>
              <a
                data-testid="hero-whatsapp-direct-button"
                href={whatsappLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="glow-button flex items-center gap-2.5 rounded-full border border-slate-600/60 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-100 hover:border-emerald-400/50"
              >
                <MessageCircle className="h-4.5 w-4.5 text-emerald-400" />
                {t("hero.ctaWhatsapp")}
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-5 flex items-center gap-2 font-tech text-xs tracking-wider text-slate-500"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              {t("hero.meetingNote")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-12 grid max-w-lg grid-cols-3 divide-x divide-emerald-500/15 rounded-2xl border border-emerald-500/15 bg-black/30"
            >
              {t("hero.stats").map((stat, i) => (
                <div key={i} data-testid={i === 0 ? "hero-stat-projects" : i === 1 ? "hero-stat-uptime" : "hero-stat-support"} className="px-5 py-5 text-center">
                  <div className="font-display text-2xl font-semibold text-emerald-400 sm:text-3xl">{stat.value}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-500">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:col-span-5 lg:block"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-emerald-500/15 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
                <img src={BRAND.heroImage} alt="New Era engineering" className="h-[480px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030706] via-transparent to-transparent" />
                <div className="glass absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-xl border border-emerald-500/20 px-5 py-4">
                  <div>
                    <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-emerald-400">NEW ERA / OPS</div>
                    <div className="mt-1 text-sm font-medium text-slate-100">{t("footer.status")}</div>
                  </div>
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
